import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { motion } from "motion/react";

import Page from "../../layout/Page";
import { FormGroup, Input, Label } from "reactstrap";
import Herobox from "../../components/Herobox";
import ContentSection from "../../components/ContentSection";
import ProductGrid from "../../components/ProductGrid";
import {
  useBjelinTranslations,
  useProductTranslations
} from "../../hooks/TranslationHooks";
import debounce from "lodash/debounce";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { articleRedirectBaseUrl } from "../../config/urls";
import Icon from "../../components/Icon";
import { ProductPreviewLoadingPlaceholder } from "../../components/ProductPreview";
import { gtmSendEvent, itemFromProduct } from "../../utils/GTMUtils";
import { useAlgoliaProductSearch } from "../../services/algoliaService";
import {
  categoryIsListable,
  getProductCategoryPedigree,
  productCategoryHasParent,
  productCategoryTitle,
  productCategoryUrl
} from "../../utils/ProductCategoryUtils";
import find from "lodash/find";
import { useProductCategories } from "../../hooks/ProductCategoryHooks";
import { useUserProducts } from "../../hooks/ProductHooks";
import {
  faqUrl,
  populateFaqSearchResult,
  useFaqData
} from "../../hooks/FaqHooks";
import { useLang } from "../../hooks/LangHooks";
import BjelinButton from "../../components/BjelinButton";
import { onKey } from "../../utils/FormUtils";

function SearchPage() {
  const t = useBjelinTranslations();
  const location = useLocation();

  return (
    <Page name="search-page" key={"search"} title={t("search")} noindex={true}>
      <Herobox type="slim" textShadow={true} className={"text-center"}>
        <h1>{t("search")}</h1>
      </Herobox>
      <ContentSection padded sidePadded>
        <SearchContent queryParam={location.search} />
      </ContentSection>
    </Page>
  );
}

const collapsedFaqArticles = 5;

function SearchResult({ query = "", displayAsOverlay = false, callback }) {
  const lang = useLang();
  const tp = useProductTranslations();
  const {
    products,
    categories: _categories,
    faqArticles: _faqArticles
  } = useAlgoliaProductSearch(query);
  const faqData = useFaqData(lang);
  const activeProducts = useUserProducts();
  const productCategories = useProductCategories();
  const t = useBjelinTranslations();
  const categories = useMemo(() => {
    return Array.isArray(_categories)
      ? _categories
          .map(nameOrId =>
            find(productCategories, category => {
              return category.name === nameOrId || category._id === nameOrId;
            })
          )
          .filter(c => c !== undefined)
          .filter(category => categoryIsListable(category, activeProducts))
      : [];
  }, [_categories, productCategories, activeProducts]);
  const faqSearchResult = useMemo(() => {
    return populateFaqSearchResult(faqData, _faqArticles);
  }, [faqData, _faqArticles]);

  const [collapseFaqArticles, setCollapseFaqArticles] = useState(true);
  const toggleCollapseFaqArticles = () =>
    setCollapseFaqArticles(!collapseFaqArticles);

  useEffect(() => {
    if (typeof callback === "function") {
      callback(Array.isArray(products) ? products : false);
    }
  }, [callback, products]);

  return (
    <>
      {query.trim().length > 2 && Array.isArray(categories) && Array.isArray(products) && (
        <div key="result">
          <motion.div
            style={{ overflowY: "auto", overflowX: "hidden" }}
            animate={{
              height: displayAsOverlay ? "calc(100dvh - 200px)" : "auto"
            }}
            initial={{ height: 0 }}
          >
            {categories.length === 0 &&
              products.length === 0 &&
              faqSearchResult.length === 0 && (
                <div key="empty" className="empty-msg">
                  <div>
                    <Icon name="face-sad-tear" size="4x" />
                  </div>
                  <div>{t("search.no-match")}</div>
                </div>
              )}
            {faqSearchResult.length > 0 && (
              <>
                <h2>{t("search.results.faq")}</h2>
                <div className={"mb-3"}>
                  {faqSearchResult
                    .slice(
                      0,
                      collapseFaqArticles
                        ? collapsedFaqArticles
                        : faqSearchResult.length
                    )
                    .map(({ article, category }) => (
                      <div key={article.id}>
                        <Link to={faqUrl({ category, article })}>
                          {article.title}
                        </Link>
                      </div>
                    ))}
                  {faqSearchResult.length > collapsedFaqArticles && (
                    <div>
                      <BjelinButton
                        color={"light"}
                        className={"mt-2 text-small"}
                        onClick={toggleCollapseFaqArticles}
                      >
                        {t(
                          `action.show-${collapseFaqArticles ? "more" : "less"}`
                        )}
                      </BjelinButton>
                    </div>
                  )}
                </div>
              </>
            )}
            {categories.length > 0 && (
              <>
                <h2>{t("search.results.categories")}</h2>
                <div className={"mb-3"}>
                  {categories.map(category => {
                    if (
                      categories.filter(other =>
                        productCategoryHasParent(other, category)
                      ).length > 0
                    ) {
                      return null;
                    }
                    const pedigree = getProductCategoryPedigree(category);
                    return (
                      <div key={category._id}>
                        {pedigree.map((part, idx) => (
                          <>
                            {idx > 0 && <span> &gt; </span>}
                            <Link to={productCategoryUrl(part)}>
                              {productCategoryTitle(part, tp)}
                            </Link>
                          </>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {products.length > 0 && (
              <>
                <h2>{t("search.results.products")}</h2>

                <ProductGrid
                  id={"search-result"}
                  cols={3}
                  products={products}
                  noSlideShow={true}
                  noOverlay={displayAsOverlay}
                />
              </>
            )}
          </motion.div>
        </div>
      )}
    </>
  );
}

export function SearchContent({
  displayAsOverlay = false,
  queryParam = "",
  toggle
}) {
  const navigate = useNavigate();
  const t = useBjelinTranslations();
  const productCategories = useProductCategories();
  const initialQuery = queryParam.replace("?", "");
  const [query, _setQuery] = useState(initialQuery);
  const [products, _setProducts] = useState(false);
  const setProducts = products => {
    if (Array.isArray(products)) {
      gtmSendEvent({
        event: "view_search_results",
        search_term: query,
        items: products.map(product =>
          itemFromProduct(product.sku, product, null, productCategories)
        )
      });
    }
    _setProducts(products);
  };
  const setQuery = useMemo(
    () => debounce(q => _setQuery(q), 1000),
    [_setQuery]
  );
  const sendSearchEvent = useMemo(
    () =>
      debounce(q => {
        gtmSendEvent({
          event: "search",
          search_string: q
        });
      }, 2000),
    []
  );
  const input = useRef();
  const updateQuery = useCallback(() => {
    setQuery(input.current.value);
    sendSearchEvent(input.current.value);
  }, [setQuery, sendSearchEvent, input]);

  useEffect(() => {
    setTimeout(() => {
      if (input.current) {
        input.current.focus();
      }
    }, 300);
  }, []);

  const closeOverlay = useCallback(
    e => {
      if (displayAsOverlay) {
        toggle();
      }
      e.preventDefault();
    },
    [displayAsOverlay, toggle]
  );

  const openProduct = useCallback(
    e => {
      if (products && products.length === 1) {
        const prod = products[0];
        navigate(articleRedirectBaseUrl + "/" + prod.sku);
      }
      e.preventDefault();
    },
    [navigate, products]
  );

  return (
    <div role="search">
      <FormGroup>
        <Label>{t("search.field-label")}</Label>
        <Input
          type={"text"}
          role="searchbox"
          className={"input-search-query"}
          placeholder={t("search")}
          defaultValue={initialQuery}
          innerRef={input}
          onFocus={e => e.currentTarget.select()}
          onChange={updateQuery}
          onKeyDown={onKey({
            escape: closeOverlay,
            enter: openProduct
          })}
        />
      </FormGroup>
      <div>
        {query && (
          <Suspense
            fallback={
              <div className={"product-grid cols-3"} aria-busy>
                <ProductPreviewLoadingPlaceholder />
              </div>
            }
          >
            <SearchResult
              query={query}
              displayAsOverlay={displayAsOverlay}
              callback={setProducts}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}

export default SearchPage;
